// libraries
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';

import { BackTop, Button, Divider, notification, Select, Tooltip, Typography } from 'antd';
import { blue, green, red } from '@ant-design/colors';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp } from '@fortawesome/pro-solid-svg-icons';

// components
import { Spinner } from '../../../shared/spinner';
import { ConversationHeader, ConversationHeaderProps } from './conversation-header';

// services
import * as ConversationService from '../../../../services/bb-api/conversations';
import * as MessageService from '../../../../services/bb-api/message';
import * as WordTrackService from '../../../../services/bb-api/word-track';

// utils
import { txt } from '../../../../locales/i18n';

function formatDate(date: Date): string {
	const format: string = 'ddd DD/MM-YY HH:mm';
	const dateString: string = moment(date + 'Z').format(format);
	return dateString;
}

const statuses: Record<string, any>[] = [
	{ name: 'Slettet af admin', id: -4 },
	{ name: 'Slettet af bruger', id: -3 },
	{ name: 'Aktiveret af bruger', id: 1 },
	{ name: 'Aktiveret af admin', id: 2 },
];

const textHighlighter = (message: string, words: string[]) => {
	const alphaNumerics = '[\\wæøåÆØÅ]'; // 0-9 & a-Å
	const wordsNormalized = words.map((word) => word.replace(/^%|%$/gm, alphaNumerics + '*'));
	const patternString = `(?:[^\\wæøåÆØÅ])(${wordsNormalized.join('|')})(?!${alphaNumerics})`;
	const pattern = new RegExp(patternString, 'mi');

	const textHighlighted = (' ' + message).replace(pattern, (match) => {
		const [suffix, ...word] = match.split('');
		return suffix + `<span class="word-highlight">${word.join('')}</span>`;
	});

	return textHighlighted.replace(/^ /, '');
};

export function ConversationRoute(): JSX.Element {
	const [user, setUser] = useState<Record<string, any>>({});
	const [loading, setLoading] = useState<boolean>(true);
	const [conversation, setConversation] = useState<Record<string, any>>({});
	const [wtWords, setWtWords] = useState<string[]>([]);

	const history = useHistory();
	const { id, userId } = useParams<Record<string, any>>();

	const handleGetConversation = useCallback(getConversationCallback, [history, id, userId]);

	useEffect(() => {
		handleGetConversation();
	}, [handleGetConversation]);

	async function getConversationCallback(): Promise<void> {
		setLoading(true);

		try {
			const conversation = await ConversationService.get(id);
			const currentUser = conversation.users.find((user: Record<string, any>) => {
				return user.id === Number(userId);
			});

			const wtWords = await WordTrackService.getWords();
			const wtWordsNormalized = wtWords.map(({ word }) => word);
			setWtWords(wtWordsNormalized);

			setUser(currentUser);
			setConversation(conversation);
		} catch (error) {
			const message: string = (error as Error).name;
			const description: string = (error as Error).message;
			notification.error({ message, description });
			history.goBack();
		}

		setLoading(false);
	}

	if (loading === true) {
		return <Spinner />;
	}

	const conversationMetadata: ConversationHeaderProps = {
		username: user.username,
		userId: userId,
		userMunicipality: user.municipality,

		creatorUsername: conversation.creatorUsername,
		creatorId: conversation.creatorId,
		creatorMunicipality: conversation.userMunicipality,

		created: conversation.created,
		statusId: conversation.status_id,

		memberAmount: conversation.users.length,
		messageAmount: conversation.messages.length,

		members: conversation.users,
	};

	return (
		<StyledMain>
			<ConversationHeader {...conversationMetadata} />
			<Divider />

			<section className="message-container">
				{conversation.messages.map((message: Record<string, any>) => {
					const isTargetMessage: boolean = message.userId === Number(userId);

					return (
						<Message
							key={message.id}
							isTarget={isTargetMessage}
							wtWords={wtWords}
							{...message}
						/>
					);
				})}
			</section>

			<BackTop target={(): any => document.querySelector('main')} />
		</StyledMain>
	);
}

function Message(props: any): JSX.Element {
	const [loading, setLoading] = useState<boolean>(false);
	const [status, setStatus] = useState<number>(props.status_id);
	const [wt, setWt] = useState<string | null>(null)

	const messageRef = useRef<HTMLDivElement>(null);
	const location = useLocation();
	const history = useHistory()

	useEffect(() => {
		const message = messageRef.current;
		if (message === null) return;

		const query = new URLSearchParams(location.search)
		if (query.get('message') !== String(props.id)) return

		if (query.has('wt')) setWt(query.get('wt'))
		message.scrollIntoView()
	}, [location.hash, location.search, props.id])

	async function handleStatusChange(status: number): Promise<void> {
		setLoading(true);

		try {
			await MessageService.update(props.id, status);
			setStatus(status);
			notification.success({ message: txt('notification.update successful') });
		} catch (error) {
			notification.error({ message: txt('notification.update failed') });
		}

		setLoading(false);
	}

	const handleApprove = async () => {
		setLoading(true)

		try {
			await WordTrackService.approveMessage(Number(wt))
			notification.success({ message: txt('notification.update successful') })
			history.push('/word-track')
		} catch (error) {
			notification.error({ message: txt('notification.update failed') })
		}

		setLoading(false)
	}

	return (
		<div className={`message ${props.isTarget && 'target-message'}`} ref={messageRef}>
			<div className="message-header">
				<div className="details-container">
					<Tooltip title={txt('global.open user')}>
						<Link to={`/users/${props.userId}`}>{props.username}</Link>
					</Tooltip>
					<Typography.Text type="secondary">{formatDate(props.created)}</Typography.Text>
				</div>

				<Tooltip title="Status">
					<Select
						loading={loading}
						defaultValue={status}
						onChange={handleStatusChange}
						dropdownMatchSelectWidth={false}
						bordered={false}
					>
						{statuses.map((status: any) => (
							<Select.Option key={status.id} value={status.id}>
								{status.name}
							</Select.Option>
						))}
					</Select>
				</Tooltip>
			</div>

			<div
				className={`message-body ${status <= 0 && 'hidden-message'}`}
				dangerouslySetInnerHTML={{ __html: textHighlighter(props.body, props.wtWords) }}
			/>

			{wt !== null && (
				<span className={props.isTarget && 'flex-end'}>
					<Button type="primary" className="approve-button" onClick={handleApprove}>
						{txt('global.approve')} <FontAwesomeIcon icon={faArrowUp} />
					</Button>
				</span>
			)}
		</div>
	)
}

const StyledMain = styled.main`
	padding: 1rem;
	display: flex;
	flex-direction: column;

	.message-container {
		padding-inline: 1ch;
		box-sizing: border-box;
		align-self: center;
		display: flex;
		flex-direction: column;
		gap: 1.8rem;
		max-width: 32rem;
		width: 100vw;
	}

	.message {
		max-width: 80%;
		align-self: flex-start;
		display: flex;
		flex-direction: column;
	}

	.message-header {
		display: flex;
		flex-direction: column;

		.details-container {
			display: flex;
			gap: 1ch;
		}
	}

	.message-body {
		padding: 1em;
		border-radius: 1em 1em 1em 0;
		background-color: ${blue[4]};
		color: #fff;
		white-space: pre-wrap;
		width: fit-content;
	}

	.hidden-message {
		opacity: 0.4;
	}

	.message-body.message {
		align-self: flex-end;
	}

	.target-message {
		align-self: flex-end;
	}

	.target-message .message-body {
		background: #eee;
		color: #000;
		align-self: flex-end;
		border-radius: 1em 1em 0 1em;
	}

	span.word-highlight {
		color: ${red.primary};
		background-color: ${red[0]};
	}

	.flex-end {
		align-self: flex-end;
	}

	.approve-button {
		background: ${green.primary};
		border-color: ${green.primary};
		display: flex;
		gap: 1ch;
		align-items: center;
		margin-top: 1ch;
	}
`
